import React from 'react';

import VideoIcon from '@rover/icons/svg/video.svg';
import { type BoxProps, Heading, Link, Paragraph } from '@rover/kibble/core';
import Banner from '@rover/kibble/official-patterns/Banner';

import { useIntroVideoProfilePromptContent } from '../content';

export const INTRO_VIDEO_PROFILE_PROMPT_TEST_ID = 'intro-video-profile-prompt';

export type Props = Omit<BoxProps, 'tx' | 'variant' | 'onPress'> & {
  /**
   * The manage screen's web URL. Web needs it so the CTA is a focusable link that Kibble
   * navigates to after `onPress`; native routes in `onPress` instead and leaves it unset.
   */
  href?: string;
  onPress: () => void;
};

/**
 * Nudges a sitter looking at their own profile to record an intro video.
 *
 * Renders unconditionally: `introVideoPrompt` on the profile payload is the whole rule,
 * so the two platforms cannot drift on it and it changes without an app-store release.
 *
 * The heading avoids Banner's `title`, a `Paragraph`, which on native emits a numeric
 * weight alongside the semibold Averta face and iOS bolds it twice over.
 */
const IntroVideoProfilePrompt = ({ href, onPress, ...containerProps }: Props): JSX.Element => {
  const { heading, body, cta } = useIntroVideoProfilePromptContent();

  return (
    <Banner
      testID={INTRO_VIDEO_PROFILE_PROMPT_TEST_ID}
      severity="info"
      icon={VideoIcon}
      containerProps={containerProps}
      suppressDefaultTypography
      FooterComponent={
        <Link size="100" href={href} onPress={onPress}>
          {cta}
        </Link>
      }
    >
      <Heading size="100" mb="1x" webProps={{ as: 'h2' }}>
        {heading}
      </Heading>
      <Paragraph size="100" mb="0x">
        {body}
      </Paragraph>
    </Banner>
  );
};

export default IntroVideoProfilePrompt;
